import React, { useMemo } from 'react';

import i18n from '@nextroll/ar-i18n';
import { Option, Select } from '@nextroll/ar-style-base';


export const AccountActionCrmRecordTypeSection = ({ crm: rawCrm, value, onUpdate, showDescription, showError }: {
    crm?: string;
    value?: string;
    onUpdate?: (value: string) => void;
    showDescription?: boolean; // show description only when 1) a valid record type is selected, and 2) no field mapping errors
    showError?: boolean;
}) => {
    const options = useMemo(() => [
        {
            value: 'Contact And Account',
            title: rawCrm === 'salesforce' ? i18n.gettext('Contact and Account') : i18n.gettext('Contacts and Companies'),
            info: rawCrm === 'salesforce'
                ? i18n.gettext('New Contacts are linked to existing Accounts. Create Accounts if not in CRM.')
                : i18n.gettext('New Contacts are linked to existing Companies. Create Companies if not in CRM.'),
        },
        rawCrm === 'salesforce' && {
            value: 'Lead And Account',
            title: i18n.gettext('Lead and Account'),
            info: i18n.gettext('Create Lead records. Create Accounts if not in CRM.'),
        },
        {
            value: 'Contact',
            title: i18n.gettext('Contact'),
            info: rawCrm === 'salesforce'
                ? i18n.gettext('Create Contact records at existing Accounts. No new Accounts are added.')
                : i18n.gettext('Create Contact records at existing Companies. No new Companies are added.'),
        },
        rawCrm === 'salesforce' && {
            value: 'Lead',
            title: i18n.gettext('Lead'),
            info: i18n.gettext('Create Lead records. No new Accounts are added.'),
        },
    ].filter(Boolean) as { value: string; title: string; info: string }[], [rawCrm]);

    const crm = useMemo(() => (rawCrm === 'salesforce' ? 'Salesforce' : 'HubSpot'), [rawCrm]);

    const description = useMemo(() => {
        if (value === 'Contact' && rawCrm === 'salesforce') {
            return i18n.gettext('When new contacts are created, they will be linked to associated accounts in Salesforce. No new accounts are created.');
        }
        if (value === 'Contact') { // HubSpot
            return i18n.gettext('When new contacts are created, they will be linked to associated companies in HubSpot. No new companies are created. These contacts will be set as Non-Marketing contacts.');
        }
        if (value === 'Contact And Account' && rawCrm === 'salesforce') {
            return i18n.gettext('When new contacts are created, they will be linked to associated accounts in Salesforce. If an account does not exist in Salesforce, a new account record will be created, and the new contacts will be associated with it.');
        }
        if (value === 'Contact And Account') { // HubSpot
            return i18n.gettext('When new contacts are created, they will be linked to associated companies in HubSpot. If a company does not exist in HubSpot, a new company record will be created, and the new contacts will be associated with it. These contacts will be set as Non-Marketing contacts.');
        }
        if (value === 'Lead') {
            return i18n.gettext('All contacts will be created as Leads. No new accounts are created.');
        }
        return i18n.gettext('All contacts will be created as Leads. If an account matching your trigger criteria does not exist in {{ crm }}, a new account record will be created.', { crm });
    }, [rawCrm, crm, value]);

    const isError = useMemo(() => showError && !value, [value, showError]);

    return (
        <>
            <label className='control-label'>{i18n.gettext('Record Type')}</label>
            <div className='workflow-node-panel-select-dropdown'>
                <Select
                    i18n={i18n}
                    placeholder={i18n.gettext('Select Record Type')}
                    value={value}
                    onOptionSelect={onUpdate}
                    className={isError ? 'error' : ''}
                >
                    {options.map(option => (
                        <Option
                            key={`account-action-crm-record-type-option${option.value}`}
                            {...option}
                            selected={value === option.value}
                        >
                            {option.title}
                        </Option>
                    ))}
                </Select>
            </div>
            {isError && (
                <p className='workflow-node-panel-select-dropdown-error-msg'>
                    {i18n.gettext('The record type is required.')}
                </p>
            )}

            {showDescription && (
                <>
                    <br />
                    <p><strong>{i18n.gettext('Record Creation')}</strong></p>
                    <p>{description}</p>
                </>
            )}
        </>
    );
};
